import { useId, useLayoutEffect, useRef, useState } from 'react'
import { Check, DragGrip, Edit, Plus, TrashDelete } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { IconButton } from '@/components/icon-button/IconButton'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/Popover'
import { Tag } from '@/components/tag/Tag'
import type { ColumnTag } from '../schemas'
import { kPresetTagColors, nextTagColor } from '../utils/tag-colors'
import TableTag from './TableTag'

export type DraftTag = ColumnTag & { id: number }
const kInputClass = 'min-w-0 rounded border border-brand-neutral-300 bg-white px-2 py-1.5 text-sm outline-none focus:border-brand-400 focus:ring-0'

function TagOptions({ tag, onChange, onDelete }: { tag: DraftTag; onChange: (tag: DraftTag) => void; onDelete: () => void }) {
  const [hex, setHex] = useState(tag.custom_color ?? '')
  const hexInputRef = useRef<HTMLInputElement>(null)
  const customFieldRef = useRef<HTMLDivElement>(null)
  const revealCustomField = useRef(false)
  useLayoutEffect(() => {
    if (!revealCustomField.current) return
    revealCustomField.current = false
    hexInputRef.current?.focus({ preventScroll: true })
    customFieldRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [tag])
  const normalizedHex = `#${hex.trim().replace(/^#/, '')}`
  const validHex = /^#[\da-f]{6}$/i.test(normalizedHex)
  const custom_color = tag.custom_color ?? (validHex ? normalizedHex : '#8B5CF6')
  const applyCustomColor = (value: string) => {
    setHex(value)
    const normalized = `#${value.trim().replace(/^#/, '')}`
    if (/^#[\da-f]{6}$/i.test(normalized)) onChange({ ...tag, custom_color: normalized })
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <input
          aria-label="Tag name"
          className={`${kInputClass} flex-1`}
          value={tag.label}
          onChange={(event) => onChange({ ...tag, label: event.target.value })}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.preventDefault()
          }}
        />
        <IconButton
          type="button"
          variant="plain"
          color="neutral"
          size="sm"
          icon={<TrashDelete />}
          aria-label="Delete tag"
          tooltipText="Delete tag"
          onClick={onDelete}
        />
      </div>
      <div className="border-t border-brand-neutral-200 pt-2">
        <p className="px-2 pb-1 text-xs font-semibold uppercase text-brand-neutral-600">Color</p>
        <div className="flex flex-col gap-0.5">
          {kPresetTagColors.map((color) => (
            <Button
              key={color}
              type="button"
              variant="plain"
              color="neutral"
              size="sm"
              className="h-[30px] justify-start !gap-2.5 !px-2 !font-normal aria-pressed:bg-brand-50"
              aria-pressed={!tag.custom_color && tag.color === color}
              onClick={() => onChange({ ...tag, color, custom_color: null })}
              leadingIcon={
                <Tag color={color} size="xs">
                  <span aria-hidden className="block h-3 w-2.5" />
                </Tag>
              }
              trailingIcon={!tag.custom_color && tag.color === color ? <Check aria-hidden className="ml-auto size-4 text-brand-500" /> : undefined}
            >
              <span className="flex-1 text-left">{color}</span>
            </Button>
          ))}
          <Button
            type="button"
            variant="plain"
            color="neutral"
            size="sm"
            className="h-[30px] w-full justify-start !gap-2.5 !px-2 !font-normal aria-pressed:bg-brand-50"
            aria-pressed={Boolean(tag.custom_color)}
            onClick={() => {
              revealCustomField.current = true
              onChange({ ...tag, custom_color })
            }}
            trailingIcon={tag.custom_color ? <Check aria-hidden className="size-4 text-brand-500" /> : undefined}
          >
            <span className="flex-1 pl-7 text-left">Custom</span>
          </Button>
        </div>
      </div>
      {tag.custom_color && (
        <div ref={customFieldRef} className="flex flex-col gap-2 pl-9 pr-2 pb-1">
          <div className="flex h-8 items-center gap-2 rounded border border-brand-neutral-300 px-1.5 focus-within:border-brand-400">
            <label className="relative size-5 shrink-0 cursor-pointer rounded" style={{ backgroundColor: custom_color }} title="Pick a color">
              <input
                aria-label="Choose custom color"
                type="color"
                value={custom_color}
                onChange={(event) => applyCustomColor(event.target.value)}
                className="absolute inset-0 size-5 cursor-pointer opacity-0 focus-visible:opacity-100"
              />
            </label>
            <input
              ref={hexInputRef}
              aria-label="Custom hex color"
              aria-invalid={Boolean(hex) && !validHex}
              placeholder="#8B5CF6"
              className="min-w-0 flex-1 border-0 bg-transparent p-0 font-mono text-xs focus:outline-none focus:ring-0"
              value={hex}
              onChange={(event) => applyCustomColor(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.preventDefault()
              }}
            />
          </div>
        </div>
      )}
      {tag.custom_color && hex && !validHex && (
        <p role="alert" className="text-xs text-red-700">
          Enter a six-digit hex color, such as #8B5CF6.
        </p>
      )}
    </div>
  )
}

export default function ColumnTagsInput({
  analysis,
  disabled = false,
  tags,
  onChange: setTags,
}: {
  analysis: boolean
  disabled?: boolean
  tags: DraftTag[]
  onChange: (tags: DraftTag[]) => void
}) {
  const [input, setInput] = useState('')
  const [openTag, setOpenTag] = useState<number | null>(null)
  const nextId = useRef(Math.max(-1, ...tags.map((tag) => tag.id)) + 1)
  const draggedTag = useRef<number | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const id = useId()
  const addTags = (value: string) => {
    const additions = value
      .split(',')
      .map((label) => label.trim())
      .filter(Boolean)
      .map((label, index) => {
        const id = nextId.current++
        return { id, label, color: nextTagColor(tags.length + index), custom_color: null }
      })
    setTags([...tags, ...additions])
    setInput('')
  }
  const moveTag = (from: number, to: number) => {
    if (disabled || from < 0 || to < 0 || to >= tags.length || from === to) return
    const next = [...tags]
    const [tag] = next.splice(from, 1)
    next.splice(to, 0, tag)
    setTags(next)
  }
  return (
    <section aria-labelledby={`${id}-label`} className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <h3 id={`${id}-label`} className="text-sm font-semibold">
          Tags
        </h3>
        <p className="text-xs text-brand-neutral-600">{analysis ? 'The AI picks from these. Drag to reorder.' : 'Drag to reorder.'}</p>
      </div>
      <div className="min-w-0 rounded-md border border-brand-neutral-300">
        {tags.length > 0 && (
          <ul aria-label="Tags" className="max-h-52 overflow-y-auto p-1">
            {tags.map((tag, index) => (
              <li
                key={tag.id}
                className={`group/tag-row flex min-w-0 items-center rounded ${disabled ? '' : 'hover:bg-brand-neutral-100 focus-within:bg-brand-neutral-100'}`}
                onDragOver={(event) => {
                  if (!disabled && draggedTag.current !== null) event.preventDefault()
                }}
                onDrop={(event) => {
                  if (draggedTag.current === null) return
                  event.preventDefault()
                  moveTag(
                    tags.findIndex((item) => item.id === draggedTag.current),
                    index
                  )
                  draggedTag.current = null
                }}
              >
                <IconButton
                  type="button"
                  variant="plain"
                  color="neutral"
                  size="sm"
                  icon={<DragGrip />}
                  aria-label={`Reorder ${tag.label}`}
                  tooltipText="Drag to reorder, or use the up and down arrow keys"
                  disabled={disabled}
                  draggable={!disabled}
                  onDragStart={(event) => {
                    draggedTag.current = tag.id
                    event.dataTransfer.effectAllowed = 'move'
                    event.dataTransfer.setData('text/plain', String(tag.id))
                  }}
                  onDragEnd={() => {
                    draggedTag.current = null
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                      event.preventDefault()
                      moveTag(index, index + (event.key === 'ArrowUp' ? -1 : 1))
                    }
                  }}
                />
                <Popover open={openTag === tag.id} onOpenChange={(open) => setOpenTag(open ? tag.id : null)}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="plain"
                      color="neutral"
                      size="sm"
                      className="!h-auto min-h-9 min-w-0 flex-1 !justify-start !gap-1 !px-1 !py-1"
                      aria-label={`Edit ${tag.label}`}
                      disabled={disabled}
                    >
                      <span className="min-w-0 flex-1 whitespace-normal text-left">
                        <TableTag tag={tag}>
                          <span className="min-w-0 [overflow-wrap:anywhere]">{tag.label}</span>
                        </TableTag>
                      </span>
                      <Edit
                        aria-hidden
                        className="size-4 shrink-0 opacity-0 group-hover/tag-row:opacity-100 group-focus-within/tag-row:opacity-100 [@media(hover:none)]:opacity-100"
                      />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    aria-label="Edit tag"
                    onOpenAutoFocus={(event) => {
                      event.preventDefault()
                      if (event.currentTarget instanceof HTMLElement) event.currentTarget.focus({ preventScroll: true })
                    }}
                    align="end"
                    width="auto"
                    showArrow={false}
                    className="w-[252px] max-w-[calc(100vw-32px)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto border border-brand-neutral-200 p-2"
                  >
                    <TagOptions
                      tag={tag}
                      onChange={(updated) => setTags(tags.map((item) => (item.id === tag.id ? updated : item)))}
                      onDelete={() => {
                        setTags(tags.filter((item) => item.id !== tag.id))
                        setOpenTag(null)
                        inputRef.current?.focus()
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </li>
            ))}
          </ul>
        )}
        <div
          className={`flex items-center gap-2 rounded px-2 py-2 focus-within:ring-1 focus-within:ring-inset focus-within:ring-brand-400 ${
            tags.length ? 'border-t border-brand-neutral-200' : ''
          }`}
        >
          <Plus className="size-4 shrink-0 text-brand-neutral-600" aria-hidden />
          <input
            ref={inputRef}
            aria-label="Add a tag"
            aria-describedby={`${id}-help`}
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm outline-none placeholder:text-brand-neutral-500 focus:ring-0"
            placeholder="Add a tag…"
            value={input}
            disabled={disabled}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                event.preventDefault()
                addTags(input)
              }
            }}
            onPaste={(event) => {
              const pasted = event.clipboardData.getData('text')
              if (!pasted.includes(',')) return
              event.preventDefault()
              const { selectionStart, selectionEnd } = event.currentTarget
              addTags(input.slice(0, selectionStart ?? input.length) + pasted + input.slice(selectionEnd ?? input.length))
            }}
          />
        </div>
      </div>
      <div id={`${id}-help`}>
        <InlineMessage>Press Enter to add. Paste a comma-separated list to add multiple.</InlineMessage>
      </div>
    </section>
  )
}
